/* eslint-disable react/button-has-type */
/* eslint-disable no-unused-vars */
/* eslint-disable function-paren-newline */
/* eslint-disable operator-linebreak */
/* eslint-disable implicit-arrow-linebreak */
// eslint-disable no-unused-vars
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RegularButton, DatePicker, SearchInput, AlertAdapter } from 'pg-ui-library';

import { useMutation, useQuery } from '@tanstack/react-query';
import PaymentsService from 'src/services/payments.service';
import { useTypedSelector } from 'src/hooks/useTypedSelector';

import { DateTime } from 'luxon';
import { IChartData } from 'src/interfaces';
import useDebounce from 'src/hooks/useDebounce';
import useRolePermission from 'src/hooks/useRolePermission';
import { RolePermission } from 'src/components/sidebar';
import {
  Container,
  Header,
  PageTitle,
  HeaderRightItems,
  TopContent,
  DetailsContainer,
  CardTitle,
  NotificationsContainer,
  ChartContainer,
  TableContent,
  TitleContainer,
} from './styles';
import PayHistoryDetails from './components/details';
import PayHistoryNotification from './components/notifications';
import { notificationMockData } from './mock/notifications.mock';

import PayHistoryChart from './components/chart';
import PayHistoryTable from './components/pay-history-table';

const PayHistory: React.FC = () => {
  const [startDate, setStartDate] = useState(DateTime.now().minus({ week: 8 }).toJSDate());
  const [endDate, setEndDate] = useState(new Date());
  const [pieChartData, setPieChartData] = useState<IChartData[]>([]);

  const { config } = useTypedSelector(state => state.config.data);

  const isoStartDate = startDate.toISOString();
  const isoEndDate = endDate.toISOString();
  const companyId = config?.selectedCompanyId || '';

  const [searchTerm, setSearchTerm] = useState('');
  // Debounce the search term so that we only perform the search
  // after the user has stopped typing for 500ms
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const {
    data: payHistoryChartData,
    refetch,
    isLoading,
    isFetching,
  } = useQuery(['employee-pay-history/pie-chart'], () =>
    PaymentsService.getCompanyPayHistoryChart({
      startDate: isoStartDate,
      endDate: isoEndDate,
      companyId,
    })
  );

  const {
    data: detailsData,
    isLoading: detailsIsLoading,
    refetch: detailsRefetch,
  } = useQuery(['pay-history-details'], async () => {
    const result = await PaymentsService.getPayHistoryDetails(isoStartDate, isoEndDate, companyId);
    return result.data;
  });

  useEffect(() => {
    const payHistoryChartObject = [
      {
        name: 'Employee Net Pay',
        value: payHistoryChartData?.data.netPay ?? 0,
        color: '#8017FB',
      },
      {
        name: 'Benefits',
        value: payHistoryChartData?.data.benefits ?? 0,
        color: '#EC2DFC',
      },
      {
        name: 'Deductions',
        value: payHistoryChartData?.data.deductions ?? 0,
        color: '#D02372',
      },
      {
        name: 'Employer Taxes',
        value: payHistoryChartData?.data.employerTax ?? 0,
        color: '#E70892',
      },
      {
        name: 'Employee Taxes',
        value: payHistoryChartData?.data.employeeTax ?? 0,
        color: '#FF7B9E',
      },
    ];

    setPieChartData(payHistoryChartObject);
  }, [payHistoryChartData]);

  const handleDateChange = (date: Date | null, setDate: Function) => {
    setDate(date);
  };

  const reload = () => {
    refetch();
    detailsRefetch();
  };

  const getExcelLink = () => PaymentsService.getAllPayStatements(companyId, isoStartDate, isoEndDate);

  useEffect(() => {
    reload();
  }, [startDate, endDate, companyId]);

  const { t } = useTranslation();

  const integrationMutation = useMutation(() => PaymentsService.runIntegration(), {
    onSuccess: () => {
      AlertAdapter(
        'Integration started successfully. Please note that it may take up to 10 minutes for the changes to be reflected on the ESS Portal.',
        'success'
      );
    },
    onError: e => {
      AlertAdapter('Another sync was already triggered, please wait until the batch is complete.', 'error');
    },
  });

  const runIntegration = () => {
    integrationMutation.mutate();
  };

  const { hasRolePermission } = useRolePermission();

  return (
    <Container>
      <Header>
        <TitleContainer>
          <PageTitle>{t('payHistory.pageTitle')}</PageTitle>
          {hasRolePermission(RolePermission.SUPER_ADMIN) && (
            <RegularButton onClick={() => runIntegration()} disabled={integrationMutation.isLoading}>
              {t('payHistory.header.syncPayments')}
            </RegularButton>
          )}
        </TitleContainer>

        <HeaderRightItems>
          <p>{t('payHistory.header.checkDate')}</p>

          <DatePicker
            id="Date-Start"
            name="Date-Start"
            label=""
            onChange={e => handleDateChange(e, setStartDate)}
            selected={startDate}
            maxDate={endDate}
          />
          <p>{t('payHistory.header.dateTo')}</p>
          <DatePicker
            id="Date-End"
            name="Date-End"
            label=""
            onChange={e => handleDateChange(e, setEndDate)}
            selected={endDate}
            minDate={startDate}
          />

          <RegularButton onClick={() => reload()}>{t('global.button.search')}</RegularButton>
        </HeaderRightItems>
      </Header>
      <TopContent>
        <DetailsContainer>
          <CardTitle>{t('payHistory.content.details')}</CardTitle>
          <PayHistoryDetails data={detailsData} isLoading={detailsIsLoading} />
        </DetailsContainer>
        <NotificationsContainer>
          <CardTitle>{t('payHistory.content.notifications')}</CardTitle>
          <PayHistoryNotification data={notificationMockData[0]} />
        </NotificationsContainer>
        <ChartContainer>
          <CardTitle>{t('payHistory.content.employeePay')}</CardTitle>
          <PayHistoryChart data={pieChartData} isLoading={isLoading || isFetching} />
        </ChartContainer>
      </TopContent>
      <TableContent>
        <div
          style={{
            display: 'flex',
            flex: '1',
            justifyContent: 'flex-end',
            alignItems: 'center',
            margin: '10px',
          }}
        >
          <SearchInput
            value={searchTerm}
            placeholder="Search..."
            handleChange={event => {
              let newValue = event.target.value;
              newValue = newValue.replace(/[',.]/g, '');
              setSearchTerm(newValue);
            }}
          />
          <a href={getExcelLink() as unknown as ''} download target="_blank" rel="noreferrer">
            <RegularButton type="button">{t('payHistory.button.exportPayslips')}</RegularButton>
          </a>
        </div>

        <PayHistoryTable startDate={isoStartDate} endDate={isoEndDate} searchTerm={debouncedSearchTerm} />
      </TableContent>
    </Container>
  );
};

export default PayHistory;
